import React, { useRef, useImperativeHandle, forwardRef } from "react";
import { Turnstile } from "@marsidev/react-turnstile";

const SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || "1x00000000000000000000AA";

/**
 * Invisible Cloudflare Turnstile widget. Renders a single hidden challenge
 * and exposes `.getResponse()` so the parent can grab a fresh token before
 * making the vote POST. Auto-refreshes expired tokens.
 *
 * Usage:
 *   const ref = useRef();
 *   ...
 *   <TurnstileWidget ref={ref} />
 *   const token = await ref.current.getToken();
 */
export const TurnstileWidget = forwardRef(function TurnstileWidget(props, ref) {
  const tsRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getToken: async () => {
      const inst = tsRef.current;
      if (!inst) return null;
      // If we already have a token from a previous render, use it; else refresh.
      let token = null;
      try {
        token = inst.getResponse?.() || null;
      } catch (e) { /* ignore */ }
      if (token) return token;
      try {
        inst.reset?.();
      } catch (e) { /* ignore */ }
      // Wait up to ~6s for a fresh token after reset (managed mode is async,
      // and some networks need extra time before Cloudflare issues a token).
      return await new Promise((resolve) => {
        let tries = 0;
        const tick = () => {
          tries += 1;
          let t = null;
          try { t = inst.getResponse?.() || null; } catch (e) { /* ignore */ }
          if (t) return resolve(t);
          if (tries > 40) return resolve(null);
          setTimeout(tick, 150);
        };
        tick();
      });
    },
  }));

  return (
    <div data-testid="turnstile-widget" style={{ display: "inline-block" }}>
      <Turnstile
        ref={tsRef}
        siteKey={SITEKEY}
        options={{
          theme: "light",
          appearance: "always",
          retry: "auto",
          refreshExpired: "auto",
        }}
      />
    </div>
  );
});

export default TurnstileWidget;
